import * as React from 'react'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

/*
 * Adapted from 21st.dev's "card-21" (DestinationCard): a picture card with a
 * gradient in its own theme colour rising from the bottom, white text over it
 * and a frosted button. Changes from the original:
 * - a router Link instead of <a>, so it doesn't reload the page;
 * - the travel props (location, flag, stats) become title, meta and ctaLabel;
 * - a light and a dark picture: the service drawings are made for a white or
 *   a #1A1A2E ground, so the card supplies that ground and swaps the drawing
 *   with the theme. They sit in the top of the card, uncropped, rather than
 *   filling it, so the gradient doesn't swallow the walker;
 * - sized down to fit the cells of a gallery grid.
 */
interface ImageCardProps extends React.HTMLAttributes<HTMLDivElement> {
  imageUrl: string
  darkImageUrl?: string
  title: string
  meta?: string
  ctaLabel: string
  to: string
  /** An HSL triple without the hsl(), e.g. "230 60% 25%". */
  themeColor: string
}

const ImageCard = React.forwardRef<HTMLDivElement, ImageCardProps>(
  ({ className, style, imageUrl, darkImageUrl, title, meta, ctaLabel, to, themeColor, ...props }, ref) => {
    const picture = 'h-full w-full object-contain object-top transition-transform duration-500 ease-in-out group-hover:scale-110'
    return (
      // The 'group' class drives every hover effect inside the card.
      <div
        ref={ref}
        style={{ ...style, '--theme-color': themeColor } as React.CSSProperties}
        className={cn('group w-full h-full', className)}
        {...props}
      >
        <Link
          to={to}
          className="relative block w-full h-full overflow-hidden rounded-2xl bg-white dark:bg-[#1A1A2E] shadow-[0_0_40px_-15px_hsl(var(--theme-color)/0.5)] transition-shadow duration-500 ease-in-out group-hover:shadow-[0_0_60px_-15px_hsl(var(--theme-color)/0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {/* The drawing, zooming a little on hover. */}
          <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[62%] p-2 sm:p-3">
            <img src={imageUrl} alt="" loading="lazy" decoding="async" className={cn(picture, darkImageUrl && 'dark:hidden')} />
            {darkImageUrl && (
              <img src={darkImageUrl} alt="" loading="lazy" decoding="async" className={cn(picture, 'hidden dark:block')} />
            )}
          </div>

          {/* Themed gradient rising from the bottom. */}
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to top, hsl(var(--theme-color) / 0.95), hsl(var(--theme-color) / 0.75) 32%, transparent 55%)',
            }}
          />

          {/* White, not a token: it sits on the theme gradient in both themes. */}
          <div className="relative flex h-full flex-col justify-end p-3 sm:p-4 text-white">
            <h3 className="text-sm sm:text-lg font-bold leading-tight tracking-tight">{title}</h3>
            {meta && <p className="mt-1 text-xs sm:text-sm font-medium text-white/80 line-clamp-1">{meta}</p>}
            {/* Frosted white, not the original's tint of the theme colour: on
                the gradient of that same colour the tinted button all but
                vanished. */}
            <div className="mt-2 sm:mt-3 flex items-center justify-between rounded-lg border border-white/40 bg-white/20 px-2.5 py-1.5 sm:px-3 sm:py-2 backdrop-blur-md shadow-sm transition-all duration-300 group-hover:border-white/60 group-hover:bg-white/30">
              <span className="text-xs sm:text-sm font-semibold tracking-wide">{ctaLabel}</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </div>
          </div>
        </Link>
      </div>
    )
  },
)
ImageCard.displayName = 'ImageCard'

export { ImageCard }
